import { useState } from "react";
import { Upload, FileText, Camera, X, CheckCircle, Loader2, Shield, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_TYPES = [
  { id: "insurance_estimate", label: "Insurance Estimate (PDF)", icon: FileText, accept: ".pdf" },
  { id: "claim_letter", label: "Claim Letter", icon: FileText, accept: ".pdf,.jpg,.jpeg,.png" },
  { id: "contractor_estimate", label: "Contractor Estimate", icon: FileText, accept: ".pdf" },
  { id: "roof_photos", label: "Roof Photos", icon: Camera, accept: ".jpg,.jpeg,.png" },
  { id: "solar_photos", label: "Solar System Photos", icon: Camera, accept: ".jpg,.jpeg,.png" },
  { id: "damage_photos", label: "Damage Photos", icon: Camera, accept: ".jpg,.jpeg,.png" },
];

export default function InsuranceUploadSection() {
  const [files, setFiles] = useState({});
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    insurance_company: "",
    claim_number: "",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFileChange = (fileType, e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      // Convert files to base64
      Promise.all(
        selectedFiles.map((file) => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                data: reader.result
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then((fileData) => {
        setFiles((prev) => ({
          ...prev,
          [fileType]: [...(prev[fileType] || []), ...fileData]
        }));
      });
    }
  };

  const removeFile = (fileType, index) => {
    setFiles((prev) => ({
      ...prev,
      [fileType]: prev[fileType].filter((_, i) => i !== index)
    }));
  };

  const getTotalFiles = () => {
    return Object.values(files).reduce((acc, arr) => acc + (arr?.length || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone || !formData.address) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (getTotalFiles() === 0) {
      toast.error("Please upload at least one document");
      return;
    }

    setIsSubmitting(true);
    try {
      const uploadData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        insurance_company: formData.insurance_company,
        claim_number: formData.claim_number,
        notes: formData.notes,
        files: files,
        lead_type: "insurance_review"
      };

      await axios.post(`${API}/insurance-uploads`, uploadData);
      setIsSubmitted(true);
      toast.success("Documents uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="upload-estimate" className="py-20 md:py-28 bg-gradient-to-b from-blue-50 to-white" data-testid="upload-complete">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl p-10 border border-slate-200 shadow-xl">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="font-heading text-3xl text-slate-900 mb-4">Documents Received!</h2>
            <p className="text-slate-600 text-lg mb-6">
              Our team will review your insurance estimate and contact you within 24-48 hours with our analysis.
            </p>
            <div className="bg-emerald-50 rounded-xl p-4 text-left border border-emerald-100">
              <p className="text-emerald-700 font-medium mb-2">What happens next?</p>
              <ul className="text-slate-700 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                  We'll review your insurance estimate for completeness
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                  Identify any potentially missing items
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                  Provide recommendations for your next steps
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="upload-estimate" className="py-20 md:py-28 bg-gradient-to-b from-blue-50 to-white" data-testid="upload-estimate-section">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-blue-200">
            <FileText className="w-4 h-4" />
            Insurance Estimate Review
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-slate-900 mb-4">
            Upload Your Documents
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Upload your insurance estimate and related documents. Our experts will review them 
            and help you understand your coverage.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - File Uploads */}
          <div className="space-y-4">
            <h3 className="text-slate-900 font-semibold text-lg mb-4">Upload Documents</h3>
            
            {FILE_TYPES.map((fileType) => (
              <div key={fileType.id} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <fileType.icon className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-900 font-medium text-sm">{fileType.label}</span>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept={fileType.accept}
                      multiple
                      onChange={(e) => handleFileChange(fileType.id, e)}
                      className="hidden"
                      data-testid={`upload-${fileType.id}`}
                    />
                    <span className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                      <Upload className="w-3 h-3" />
                      Choose Files
                    </span>
                  </label>
                </div>
                
                {/* File List */}
                {files[fileType.id]?.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {files[fileType.id].map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
                        <span className="text-slate-700 text-xs truncate max-w-[200px]">{file.name}</span>
                        <button
                          onClick={() => removeFile(fileType.id, index)}
                          className="text-red-500 hover:text-red-600 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Upload Summary */}
            <div className="bg-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-blue-700 text-sm font-medium">Total Files Uploaded</span>
                <span className="text-slate-900 font-bold">{getTotalFiles()}</span>
              </div>
            </div>

            {/* Security Note */}
            <div className="flex items-start gap-3 text-slate-600 text-sm">
              <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p>Your documents are encrypted and stored securely. We never share your information without your consent.</p>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
            <h3 className="text-slate-900 font-semibold text-lg mb-6">Your Information</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full h-12 px-4 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400"
                  data-testid="insurance-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="w-full h-12 px-4 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400"
                    data-testid="insurance-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(301) 555-1234"
                    className="w-full h-12 px-4 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400"
                    data-testid="insurance-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, Bethesda, MD"
                  className="w-full h-12 px-4 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400"
                  data-testid="insurance-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Insurance Company
                  </label>
                  <input
                    type="text"
                    value={formData.insurance_company}
                    onChange={(e) => setFormData({ ...formData, insurance_company: e.target.value })}
                    placeholder="State Farm, Allstate..."
                    className="w-full h-12 px-4 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400"
                    data-testid="insurance-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Claim Number
                  </label>
                  <input
                    type="text"
                    value={formData.claim_number}
                    onChange={(e) => setFormData({ ...formData, claim_number: e.target.value })}
                    placeholder="CLM-123456"
                    className="w-full h-12 px-4 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400"
                    data-testid="insurance-claim"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any specific concerns or questions..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-slate-50 text-slate-900 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-400 resize-none"
                  data-testid="insurance-notes"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl font-bold text-lg shadow-lg shadow-blue-200"
                data-testid="insurance-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Submit for Review
                  </>
                )}
              </Button>

              {/* Disclaimer */}
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    This review is informational only and does not determine insurance coverage or legal rights. 
                    Please consult with your insurance company and/or legal counsel for official guidance.
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
